import { useCallback, useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Button, SettingsCard, Typography, useToast } from "@humansignal/ui";
import { IconCheck, IconWarning } from "@humansignal/icons";
import { useUpdatePageTitle, createTitleFromSegments } from "@humansignal/core";
import { useAuth } from "@humansignal/core/providers/AuthProvider";
import { useAPI } from "../../providers/ApiProvider";
import { ProjectContext } from "../../providers/ProjectProvider";
import { ConsentGate } from "../../components/ConsentGate/ConsentGate";
import { useTranslation } from "../../i18n/LocaleProvider";
import { LogoThumb } from "../Home/OrglessHome";
import { SummaryStrip } from "../../components/SummaryStrip/SummaryStrip";
import { cn } from "../../utils/bem";
import { askConfirm } from "../../utils/ask";
import { ProjectNav } from "./ProjectNav";
import { FacilitatorPanel } from "./FacilitatorPanel";
import { RejectedWorkPanel } from "./RejectedWorkPanel";
import { AttentionRow } from "./AttentionRow";
import { SettingsSearch } from "../Settings/SettingsSearch";
import "./ProjectDashboard.prefix.css";

const block = cn("project-dashboard");

const fmtInt = (n) => (n == null ? "—" : Number(n).toLocaleString());

/**
 * Project Dashboard — the landing page at /projects/:id. Replaces the
 * old immediate-redirect to /data. Headlines: status pill, quick-
 * action buttons (Label / Review / Publish), and an at-a-glance stat
 * strip. Role-gated so annotators see Label, managers see everything.
 */
export const ProjectDashboard = () => {
  const { project, fetchProject } = useContext(ProjectContext);
  const history = useHistory();
  const api = useAPI();
  const toast = useToast();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [publishing, setPublishing] = useState(false);
  const [descExpanded, setDescExpanded] = useState(false);
  // Crowdsource join state. MUST be declared here — above the
  // ``if (!project?.id) return`` early-return below — so the hook
  // count is identical on every render. When these lived after that
  // guard, the first render (project still loading) called fewer
  // hooks than the second (project loaded), which is exactly the
  // "rendered more hooks than during the previous render" crash
  // (React #310) on opening a project.
  const [joining, setJoining] = useState(false);

  useUpdatePageTitle(createTitleFromSegments([project?.title, "Dashboard"]));

  const role = user?.active_organization_role;
  const canManage =
    !!user &&
    (user.is_staff || user.is_superuser || ["OW", "AD", "MA"].includes(role) || project?.created_by?.id === user.id);
  const reviewEnabled = project?.review_settings?.enabled !== false;
  const canReview = (canManage || role === "RE") && reviewEnabled;
  const canLabel = !!user; // any org member that sees the project can label
  const isPublished = !!project?.is_published;
  const everPublished = !!project?.published_at;

  const [restoring, setRestoring] = useState(false);
  const restoreProject = useCallback(async () => {
    if (!project?.id || restoring) return;
    setRestoring(true);
    try {
      await api.callApi("updateProject", { params: { pk: project.id }, body: { is_archived: false } });
      toast.show({ message: "Project restored." });
      await fetchProject?.(project.id, true);
    } finally {
      setRestoring(false);
    }
  }, [api, project?.id, restoring, toast, fetchProject]);
  const togglePublished = useCallback(
    async (next) => {
      if (!project?.id || publishing) return;
      if (!next) {
        const ok = await askConfirm({
          title: "Pause this project?",
          body: "Annotators and reviewers will lose access immediately. Anyone currently on the project will be sent back to the projects list. Work already submitted is unaffected.",
          okText: "Pause",
          danger: true,
        });
        if (!ok) return;
      }
      setPublishing(true);
      const res = await api.callApi("updateProject", {
        params: { pk: project.id },
        body: { is_published: next },
      });
      setPublishing(false);
      if (!res || res.error) {
        toast.show({
          message: res?.error ?? (next ? "Couldn't publish project" : "Couldn't pause project"),
          type: "error",
        });
        return;
      }
      const verb = next ? (everPublished ? "resumed" : "published") : "paused";
      toast.show({ message: `Project ${verb}` });
      fetchProject(project.id, true);
    },
    [api, project?.id, publishing, toast, fetchProject, everPublished],
  );

  const onJoinProject = useCallback(async () => {
    if (!project?.id || joining) return;
    setJoining(true);
    const res = await api.callApi("projectJoin", {
      params: { pk: project.id },
      suppressError: true,
    });
    setJoining(false);
    if (!res || res.error) {
      toast.show({
        message: res?.error || "Couldn't join this project. Try again in a moment.",
        type: "error",
      });
      return;
    }
    toast.show({ message: "You joined this project." });
    fetchProject(project.id, true);
  }, [api, project?.id, joining, toast, fetchProject]);

  if (!project?.id) {
    return (
      <div className={block.toClassName()}>
        <div className={block.elem("loading").toClassName()}>
          {t("pages.projectdashboard.project_dashboard.loading_project")}
        </div>
      </div>
    );
  }

  const finished = project.finished_task_number ?? 0;
  const total = project.task_number ?? 0;
  const completionPct = total > 0 ? Math.round((finished / total) * 100) : 0;

  // Crowdsource surface — orgless viewer landing on a public project.
  //   - Not an org member of the project's org (user.active_organization
  //     doesn't match project.organization) AND the project is public.
  //   - If contribution_mode = open AND they haven't already joined,
  //     show a "Join to start contributing" CTA that hits /projects/:id/
  //     join then reloads project detail.
  //   - If contribution_mode = members_only, show a read-only banner
  //     with a "Contact the owner for an invite" note.
  // Members of the owning org don't see the banner at all.
  const projectOrgId = project?.organization?.id ?? project?.organization ?? null;
  const isProjectMember = !!user && (user.is_staff || user.is_superuser || user.active_organization === projectOrgId);
  const isPublic = project?.visibility === "public";
  const isOpen = project?.contribution_mode === "open";
  const isAlreadyContributor = !!project?._current_user_joined_contributor;
  const showJoinBanner = !!user && !isProjectMember && isPublic && isOpen && !isAlreadyContributor;
  const showMembersOnlyBanner = !!user && !isProjectMember && isPublic && !isOpen;

  return (
    <ConsentGate projectId={project.id}>
      <div className={block.toClassName()}>
        {/* Crowdsource banner — outside the hero so it reads as
          contextual state, not project chrome. */}
        {showJoinBanner && (
          <div className={block.elem("crowdsource-banner").toClassName()}>
            <div className={block.elem("crowdsource-banner-text").toClassName()}>
              <strong>Join this project to start contributing.</strong>
              <span>
                {" "}
                Once you join, this project shows up under your Contributions and you can begin annotating tasks.
              </span>
            </div>
            <Button look="filled" variant="primary" disabled={joining} waiting={joining} onClick={onJoinProject}>
              {joining ? "Joining…" : "Join project"}
            </Button>
          </div>
        )}
        {showMembersOnlyBanner && (
          <div className={block.elem("crowdsource-banner").mod({ readonly: true }).toClassName()}>
            <div className={block.elem("crowdsource-banner-text").toClassName()}>
              <strong>This project is public but members-only.</strong>
              <span> You can browse the description but need an invite from the project owner to annotate tasks.</span>
            </div>
          </div>
        )}
        {/* Status + primary actions */}
        <div className={block.elem("hero").toClassName()}>
          <div className={block.elem("hero-text").toClassName()}>
            {/* Publish state is conveyed by the Publish/Pause button on the
              right, so no separate status pill here. */}
            <div className={block.elem("title-row").toClassName()}>
              <LogoThumb src={project.logo} title={project.title} size={48} />
              {/* Description slots UNDER the name in the same block —
                it read as its own detached row and pushed the licence
                line down (owner feedback). */}
              <div className={block.elem("title-stack").toClassName()}>
                <h1 className={block.elem("title").toClassName()} title={project.title}>
                  {project.title}
                </h1>
                {project.is_archived && <span className={block.elem("archived-pill").toClassName()}>Archived</span>}
                {project.description && (
                  /* Clamped to 2 lines (CSS); tapping toggles the full
                   text so long descriptions stay reachable without
                   ever breaking the header layout. */
                  <p
                    className={block.elem("description").mod({ expanded: descExpanded }).toClassName()}
                    onClick={() => setDescExpanded((v) => !v)}
                    title={descExpanded ? undefined : project.description}
                  >
                    {project.description}
                  </p>
                )}
              </div>
            </div>
            <div className={block.elem("hero-meta").toClassName()}>
              {/* License chip — shown next to the title row so dataset
              consumers see at a glance how the project's outputs can
              be reused. ``project.license`` is a JSON blob set by
              the Licensing wizard at Settings → Licensing; absence
              means the owner hasn't decided yet. The "Set license"
              fallback is a small CTA pointing at the wizard so
              owners are gently nudged to choose one. */}
              {(() => {
                const lic = project.license || {};
                const isSet = !!lic.identifier;
                return (
                  <div className={block.elem("license").toClassName()}>
                    {isSet ? (
                      <a
                        className={block.elem("license-badge").toClassName()}
                        href={lic.url || `/projects/${project.id}/settings/licensing`}
                        target={lic.url ? "_blank" : undefined}
                        rel={lic.url ? "noreferrer" : undefined}
                        title={lic.summary || lic.name || lic.identifier}
                      >
                        <span aria-hidden className={block.elem("license-icon").toClassName()}>
                          ©
                        </span>
                        {lic.identifier}
                      </a>
                    ) : canManage ? (
                      <a
                        className={block.elem("license-cta").toClassName()}
                        href={`/projects/${project.id}/settings/licensing`}
                      >
                        Set dataset license →
                      </a>
                    ) : null}
                  </div>
                );
              })()}

              {/* Hugging Face Hub source pill. Reads project.hf_source,
              now a LIST of dicts (multi-dataset binding shipped in
              migration projects/0074+0075). Shows the first binding
              as the primary badge; adds a small '+N more' chip when
              the project is bound to more than one HF dataset. Click-
              through: Settings → Hub. */}
              {(() => {
                const bindings = Array.isArray(project.hf_source)
                  ? project.hf_source
                  : project.hf_source
                    ? [project.hf_source]
                    : [];
                const first = bindings.find((b) => b && b.source_repo_id);
                if (!first) return null;
                const extra = bindings.length - 1;
                return (
                  <div className={block.elem("hf-source").toClassName()}>
                    <a
                      className={block.elem("hf-badge").toClassName()}
                      href={`/projects/${project.id}/settings/hugging-face`}
                      title={`Imported from ${first.source_repo_id}, ${first.source_split || "train"}${extra > 0 ? ` (+${extra} more)` : ""}`}
                    >
                      <span aria-hidden style={{ fontSize: "var(--font-size-caption)", lineHeight: 1 }}>
                        🤗
                      </span>
                      {first.source_repo_id}
                      {extra > 0 && <span className={block.elem("hf-badge-count").toClassName()}>+{extra}</span>}
                    </a>
                  </div>
                );
              })()}
            </div>
          </div>

          <div className={block.elem("actions").toClassName()}>
            {canManage && project.is_archived && (
              <Button
                size="large"
                look="outlined"
                disabled={restoring}
                waiting={restoring}
                onClick={restoreProject}
                aria-label="Restore project"
              >
                Restore project
              </Button>
            )}
            {canManage &&
              !project.is_archived &&
              (isPublished ? (
                <Button
                  size="large"
                  look="outlined"
                  variant="negative"
                  disabled={publishing}
                  waiting={publishing}
                  onClick={() => togglePublished(false)}
                  aria-label={t("projects.pause")}
                >
                  {t("projects.pause")}
                </Button>
              ) : (
                <Button
                  size="large"
                  look="outlined"
                  variant="positive"
                  disabled={publishing}
                  waiting={publishing}
                  onClick={() => togglePublished(true)}
                  aria-label={everPublished ? t("projects.resume") : t("projects.publish")}
                >
                  {everPublished ? t("projects.resume") : t("projects.publish")}
                </Button>
              ))}
            {canReview && !project.is_archived && (
              <Button
                size="large"
                look="outlined"
                onClick={() => history.push(`/projects/${project.id}/review-stream`)}
                aria-label={t("pages.projectdashboard.project_dashboard.open_review_stream")}
              >
                Review
              </Button>
            )}
            {canLabel && !project.is_archived && (
              <Button
                size="large"
                // Annotators: labeling IS the job — make it the primary
                // filled action. Managers keep the quieter outlined set.
                look={canManage ? "outlined" : undefined}
                variant={canManage ? "positive" : undefined}
                onClick={() => history.push(`/projects/${project.id}/data?labeling=1`)}
                aria-label={t("pages.projectdashboard.project_dashboard.start_labeling")}
              >
                {canManage ? "Label" : "Continue labeling"}
              </Button>
            )}
            {/* Danger Zone moved to a quiet row at the END of the page
              (owner-requested) — a destructive entry point doesn't
              belong beside the everyday actions. */}
          </div>
        </div>
        {/* Tools row under the header: settings search on the left, the
          setup step buttons beside it (no card) until setup is done. */}
        {canManage && (
          <div className={block.elem("tools-row").toClassName()}>
            <div className={block.elem("search-row").toClassName()}>
              <SettingsSearch base={`/projects/${project.id}/settings`} />
            </div>
            <GettingStartedChecklist
              project={project}
              history={history}
              togglePublished={togglePublished}
              everPublished={everPublished}
              api={api}
            />
          </div>
        )}

        {/* First-run checklist — only shown while the project still has
          setup to do. Each step disappears as it's completed; the
          card hides entirely once every step is done. Non-managers
          don't see this (they can't do the setup actions anyway). */}

        <div className={block.elem("grid").toClassName()}>
          <div className={block.elem("main").toClassName()}>
            {/* Progress bar */}
            <SettingsCard
              title={t("pages.projectdashboard.project_dashboard.progress")}
              description={
                total > 0
                  ? `${fmtInt(finished)} of ${fmtInt(total)} tasks complete`
                  : "No tasks yet, import data to get started."
              }
            >
              <div className={block.elem("progress").toClassName()} aria-hidden>
                <div className={block.elem("progress-fill").toClassName()} style={{ width: `${completionPct}%` }} />
              </div>
              {total === 0 && canManage && (
                <Button
                  look="outlined"
                  size="small"
                  onClick={() => history.push(`/projects/${project.id}/data/import`)}
                  className="mt-base"
                  aria-label={t("pages.projectdashboard.project_dashboard.import_tasks")}
                >
                  {t("pages.projectdashboard.project_dashboard.import_tasks")}
                </Button>
              )}
              {(canManage || canReview) && (
                <div className={block.elem("summary").toClassName()}>
                  <SummaryStrip
                    items={[
                      { label: "Tasks", value: fmtInt(total) },
                      { label: "Annotated", value: fmtInt(finished) },
                      { label: "Completion", value: `${completionPct}%` },
                      {
                        label: "Annotations",
                        value: fmtInt(project.total_annotations_number),
                      },
                      {
                        label: "Skipped",
                        value: fmtInt(project.skipped_annotations_number),
                      },
                      {
                        label: "Predictions",
                        value: fmtInt(project.total_predictions_number),
                      },
                    ]}
                  />
                </div>
              )}
            </SettingsCard>
          </div>
          <div className={block.elem("side").toClassName()}>
            {/* What needs me right now — counts that link to the work. */}
            <AttentionRow projectId={project.id} canManage={canManage} canReview={canReview} canLabel={canLabel} />
          </div>
        </div>

        {/* Panels: setup checklist, facilitator cohort, rejected work —
          each renders nothing when it has nothing to say. */}
        <div className={block.elem("panels").toClassName()}>
          <FacilitatorPanel projectId={project.id} canManage={canManage} />
          <RejectedWorkPanel projectId={project.id} />
        </div>

        {/* Danger Zone entry — deliberately LAST on the page, visually
          separated, so deleting/resetting the project is a destination
          you scroll to, never a neighbor of the everyday actions.
          (Accept-all-pending also lives inside Danger Zone settings.) */}
        {canManage && (
          <div className={block.elem("danger-row").toClassName()}>
            <Button
              size="small"
              look="outlined"
              variant="negative"
              onClick={() => history.push(`/projects/${project.id}/settings/danger-zone`)}
              aria-label="Open Danger Zone settings"
            >
              Danger Zone — delete or reset this project
            </Button>
          </div>
        )}
      </div>
    </ConsentGate>
  );
};

/**
 * "Getting started" checklist shown on the dashboard while the project
 * still has setup to do. Steps disappear as they're completed; once
 * every step is done, the whole card is hidden on future visits.
 *
 * Steps detected from project state:
 *   1. Configure labeling interface — ``label_config`` is set to
 *      anything other than the empty ``<View></View>`` default.
 *   2. Import tasks — ``task_number > 0``.
 *   3. Invite team members — we can't reliably check this from the
 *      project payload, so the step is informational and hyperlinks
 *      to Members; managers can dismiss it manually if they're a
 *      one-person team.
 *   4. Publish — ``is_published`` is true.
 */
function GettingStartedChecklist({ project, history, togglePublished, everPublished, api }) {
  // Always rendered — the operator asked for the checklist to stay
  // visible even once every step is green, partly so the ongoing
  // warning on the members step (count < overlap) stays in sight.
  // No auto-hide, no dismiss button.

  // Live member count — members step is "done" only when at least
  // one explicit project member exists. We also flag the overlap
  // shortfall inline when ``maximum_annotations`` is higher than the
  // available annotator count, since the manager can't hit their
  // configured overlap without enough people on the roster.
  const [memberInfo, setMemberInfo] = useState({ total: 0, annotators: 0, loaded: false });
  useEffect(() => {
    let cancelled = false;
    if (!project?.id) return undefined;
    api
      .callApi("projectMembers", {
        params: { pk: project.id, page_size: 1, page: 1 },
        suppressError: true,
      })
      .then((res) => {
        if (cancelled || !res || res.error) return;
        const results = Array.isArray(res) ? res : (res.results ?? []);
        const total = res.count ?? results.length;
        const annotators = results.filter((m) => !m.role || m.role === "AN").length;
        setMemberInfo({ total, annotators, loaded: true });
      });
    return () => {
      cancelled = true;
    };
  }, [api, project?.id]);

  const hasConfig = !!project.label_config && project.label_config.replace(/\s/g, "") !== "<View></View>";
  const hasTasks = (project.task_number ?? 0) > 0;
  const isPublished = !!project.is_published;
  const maxAnn = project.maximum_annotations ?? 1;
  const notEnoughAnnotators = memberInfo.loaded && memberInfo.total > 0 && memberInfo.total < maxAnn;

  const membersBody = !memberInfo.loaded
    ? "Add annotators and reviewers from your organization so the workload can be split."
    : memberInfo.total === 0
      ? "Add annotators and reviewers from your organization so the workload can be split."
      : notEnoughAnnotators
        ? `${memberInfo.total} member${memberInfo.total === 1 ? "" : "s"} added, but your overlap setting requires ${maxAnn} annotations per task. Invite at least ${maxAnn - memberInfo.total} more annotator${maxAnn - memberInfo.total === 1 ? "" : "s"} or lower the overlap in Settings → Quality.`
        : `${memberInfo.total} member${memberInfo.total === 1 ? "" : "s"} on the roster.`;

  const steps = [
    {
      key: "config",
      title: "Configure labeling interface",
      body: "Pick a template (NER, classification, transcription, …) or write your own labeling config.",
      done: hasConfig,
      action: "Open Setup",
      onClick: () => history.push(`/projects/${project.id}/settings/labeling`),
    },
    {
      key: "tasks",
      title: "Import your first tasks",
      body: "Upload a CSV / JSON / TSV file, paste a dataset URL, or connect a cloud bucket.",
      done: hasTasks,
      action: "Import data",
      onClick: () => history.push(`/projects/${project.id}/data/import`),
    },
    {
      key: "members",
      title: "Invite members to label",
      body: membersBody,
      done: memberInfo.total > 0,
      // If there are already members but the count is below the
      // overlap setting, keep the step visible with a warning tone.
      warn: notEnoughAnnotators,
      action: memberInfo.total > 0 ? "Manage members" : "Invite members",
      onClick: () => history.push(`/projects/${project.id}/members`),
    },
    {
      key: "publish",
      title: "Publish the project",
      body: isPublished
        ? "You're live, annotators and reviewers can see it now."
        : "Flip the switch so annotators and reviewers can open the project.",
      done: isPublished,
      action: everPublished ? "Resume" : "Publish",
      onClick: () => togglePublished(true),
    },
  ];

  const completeCount = steps.filter((s) => s.done && !s.warn).length;
  const warnCount = steps.filter((s) => s.warn).length;
  const allDone = completeCount === steps.length;

  // Surface the first unfinished (or warning) step as the "next action"
  // so managers see one clear CTA alongside the progress strip instead
  // of scanning four rows of copy.
  const activeStep = steps.find((s) => !s.done || s.warn);

  const block = cn("project-dashboard");

  if (allDone && warnCount === 0) return null;

  return (
    <div className={block.elem("setup-row").toClassName()} aria-label="Project setup">
      <span className={block.elem("setup-row-label").toClassName()}>
        Setup {completeCount}/{steps.length}
      </span>
      {steps.map((step, i) => (
        <button
          key={step.key}
          type="button"
          onClick={step.onClick}
          title={step.body}
          aria-label={`${step.title}. ${step.action}`}
          className={block
            .elem("setup-chip")
            .mod({ done: step.done && !step.warn, warn: step.warn, active: step === activeStep })
            .toClassName()}
        >
          <span className={block.elem("setup-chip-bullet").toClassName()} aria-hidden>
            {step.warn ? <IconWarning /> : step.done ? <IconCheck /> : i + 1}
          </span>
          {step.title}
        </button>
      ))}
    </div>
  );
}

// Exposed as the route component mounted at "/" (relative to /projects/:id).
ProjectDashboard.title = "Dashboard";
ProjectDashboard.path = "/";
ProjectDashboard.exact = true;

// Rendered in the menubar's right-context slot when this page is active.
ProjectDashboard.context = ({ user: _user }) => {
  const { project } = useContext(ProjectContext);
  const { user } = useAuth();
  if (!project?.id) return null;
  const role = user?.active_organization_role;
  const canManage =
    !!user &&
    (user.is_staff || user.is_superuser || ["OW", "AD", "MA"].includes(role) || project?.created_by?.id === user.id);
  return <ProjectNav projectId={project.id} canManage={canManage} project={project} userRole={role} />;
};
